import { Button, HSpacer, IconButton, Text, VSpacer } from "@/components/DesignSystem";
import { SubwalletEndpoint } from '@api/endpoints';
import Edit from '@mui/icons-material/Edit';
import KeyboardArrowRight from '@mui/icons-material/KeyboardArrowRight';
import KeyboardArrowUp from '@mui/icons-material/KeyboardArrowUp';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Divider,
  Grid,
  Stack,
} from "@mui/material";
import { formatDateOnly } from "@shared/utilities";
import React, { useState } from 'react';
import { SubwalletProgramYearList } from './SubwalletProgramYearList';

interface RetailerWalletAccordionListItemProps {
  onEditClicked?: () => void,
  subwallet: SubwalletEndpoint.List.ResolvedApiSubwalletListItem,
}

export const SubwalletAccordionListItem = ({
  onEditClicked,
  subwallet,
}: RetailerWalletAccordionListItemProps) => {
  const [expanded, setExpanded] = useState(false);

  return (
    <Accordion
      disableGutters
      elevation={0}
      expanded={expanded}
      sx={{
        backgroundColor: "inherit",
        '&:before': {
          display: 'none',
        },
      }}
    >
      <AccordionSummary sx={{ padding: 0 }}>
        <Grid alignItems="center" container spacing={2}>
          <Grid item sm={4} xs={12}>
            <Stack alignItems="center" direction="row">
              <IconButton
                onClick={() => setExpanded(!expanded) }
                sx={{ width: '40px' }}
                testID={`subwallet-${subwallet.id}-expand-icon`}
              >
                {expanded ? <KeyboardArrowUp /> : <KeyboardArrowRight />}
              </IconButton>
              <HSpacer size="7" />
              <Text category="body-medium">
                {subwallet.name}
              </Text>
            </Stack>
          </Grid>
          <Grid item sm={3} xs={6}>
            <Text category="body-medium">
              {subwallet.manufacturer ? subwallet.manufacturer.name : subwallet.retailer.name}
            </Text>
          </Grid>
          <Grid item sm={3} xs={6}>
            <Text category="body-medium">
              {formatDateOnly(subwallet.updatedAt, 'America/New_York', 'LLL d, yyyy')}
            </Text>
          </Grid>
          <Grid item sm="auto" sx={{ marginLeft: 'auto' }}>
            <Button
              color="primary"
              onClick={onEditClicked}
              startIcon={<Edit />}
              testID={`subwallet-${subwallet.id}-edit-button`}
              variant="text"
            >
              Edit
            </Button>
          </Grid>
        </Grid>
      </AccordionSummary>
      <AccordionDetails sx={{ padding: '0px 111px 32px 64px' }}>
        <Grid container spacing={4}>
          <Grid item md={6} xs={12}>
            <Text category="title-small" fontWeight={500}>
              Redeemable products description
            </Text>
            <VSpacer size="3" />
            <Text category="body-medium">
              {subwallet.redeemableProductsDescription}
            </Text>
          </Grid>
          <Grid item md={6} xs={12}>
            <Text category="title-small" fontWeight={500}>
              Redemption SKUs
            </Text>
            <VSpacer size="3" />
            {!subwallet.skuProducts.length && (
              <Stack alignItems="center" direction="column" justifyContent="center">
                <Text category="body-medium">
                  0 redemption skus
                </Text>
              </Stack>
            )}
            {!!subwallet.skuProducts.length && (
              <>
                <Divider />
                <VSpacer size="3" />
              </>
            )}
            <Stack divider={<Divider />} spacing={1}>
              {subwallet.skuProducts.map((sku) => (
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  key={sku.id}
                  px="10px"
                >
                  <Text breakWord category="body-medium" textAlign="left">{sku.name}</Text>
                  <HSpacer size="6" />
                  <Text category="body-medium" textAlign="right" whiteSpace="nowrap">
                    {sku.externalId}
                  </Text>
                </Stack>
              ))}
            </Stack>
            {!!subwallet.skuProducts.length && (
              <>
                <VSpacer size="3" />
                <Divider />
              </>
            )}
          </Grid>
          {subwallet.retailer.programSubwalletId === subwallet.id && (
            <Grid item xs={12}>
              <SubwalletProgramYearList subwalletId={subwallet.id} />
            </Grid>
          )}
        </Grid>
      </AccordionDetails>
    </Accordion>
  );
};
